import { createRoute, type LinkProps, redirect } from '@tanstack/react-router';
import { pharmacyParentRoute } from 'pages/pharmacy/pharmacyParentRoute';
import { zodPathParamId } from 'routes/routeUtils';
import { z } from 'zod';

/**
 * Legacy pharmacy routes folded into the unified Paramètres shell
 * (`/pharmacy/settings/*`). They are kept reachable as redirects so existing
 * deep links keep landing on the right screen. Listing them here — rather than
 * one `route.ts` file per page — keeps the redirect map in a single place.
 */

const groupRedirectRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/group',
  beforeLoad: () => {
    throw redirect({ to: '/pharmacy/settings/group' });
  },
});

const lgoSettingRedirectRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/lgo_setting',
  beforeLoad: () => {
    throw redirect({ to: '/pharmacy/settings/extractor' });
  },
});

const faksKonnectRedirectRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/faks_konnect',
  validateSearch: z.object({
    source: z.enum(['trades_banner']).optional(),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/pharmacy/settings/konnect', search });
  },
});

const stripeRedirectRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/stripe',
  validateSearch: z.object({
    compensationAmountInEuroCents: z.number().optional(),
    fromScreen: z.enum(['trade']).optional(),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/pharmacy/settings/stripe', search });
  },
});

const EDIT_TAB_REDIRECTS = {
  team: '/pharmacy/settings/team',
  legal_entity: '/pharmacy/settings/billing',
  banking_information: '/pharmacy/settings/banking',
  invoices: '/pharmacy/settings/kopilot',
  note_templates: '/pharmacy/settings/note-templates',
  stock_konnect: '/pharmacy/settings/stock-konnect',
} satisfies Record<string, LinkProps['to']>;

const editRedirectRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/$id/edit',
  params: z.object({ id: zodPathParamId }),
  validateSearch: z.object({
    tab: z
      .enum([
        'team',
        'legal_entity',
        'stock_konnect',
        'invoices',
        'banking_information',
        'note_templates',
      ])
      .default('team'),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: EDIT_TAB_REDIRECTS[search.tab] });
  },
});

export const legacySettingsRedirectRoutes = [
  groupRedirectRoute,
  lgoSettingRedirectRoute,
  faksKonnectRedirectRoute,
  stripeRedirectRoute,
  editRedirectRoute,
];
